import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useStore } from '@shared/hooks/useStore';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { flowResult } from 'mobx';
import { ArrowLeft } from 'lucide-react';
import { PasswordInput } from '@shared/components/common/Input';
import { Button } from '@shared/components/common/Button';
import { Label } from '@shared/components/common/Label';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage
} from '@shared/components/common/Form';
import SuccessIcon from '@shared/assets/images/success_icon.svg';
import { ADMIN_ROUTES } from '@shared/configs/constants';
import { PostConfirmPasswordRequest } from '@shared/types/http-payload/auth';
import NotFoundPage from '@admin/pages/not-found';
import { PASSWORD_REGEX } from '@/shared/configs/regex';

const confirmPasswordSchema = yup.object({
    password: yup
        .string()
        .required('必須項目をご入力ください')
        .matches(
            new RegExp(PASSWORD_REGEX),
            'アルファベットの小文字・大文字・数字を含む8～20文字でご入力ください'
        ),
    passwordConfirm: yup
        .string()
        .oneOf([yup.ref('password')], '上記パスワードと一致しないため再入力をお願いします。')
        .required('必須項目をご入力ください')
});

type ConfirmPasswordFormData = yup.InferType<typeof confirmPasswordSchema>;

const ConfirmPasswordPage = observer(() => {
    const { authStore } = useStore();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const uuid = params?.get('uuid') || null;

    const [isLoading, setIsLoading] = useState(false);
    const [isCheckUuidOk, setIsCheckUuidOk] = useState(true);
    const [isConfirmOk, setIsConfirmOk] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    const form = useForm<ConfirmPasswordFormData>({
        resolver: yupResolver(confirmPasswordSchema),
        mode: 'onChange',
        defaultValues: {
            password: '',
            passwordConfirm: ''
        }
    });

    useEffect(() => {
        onCheckUuid();
    }, []);

    const onCheckUuid = async () => {
        const res = await flowResult(authStore.getCheckUuidConfirmPassword({ uuid }));
        if (!res) {
            setIsCheckUuidOk(false);
        }
    };

    const onSubmit = async (data: ConfirmPasswordFormData) => {
        setIsLoading(true);
        try {
            const body: PostConfirmPasswordRequest = {
                password: data.password || '',
                uuid: uuid || ''
            };
            const res = await flowResult(authStore.confirmPassword(body));
            if (res) {
                setIsConfirmOk(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        localStorage.removeItem('token');
        navigate(ADMIN_ROUTES.login.href);
    };

    if (!uuid || !isCheckUuidOk) {
        return <NotFoundPage />;
    }

    return (
        <div className='min-h-screen flex items-center justify-center bg-[#F1F1F1]'>
            <div className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] md:p-10 p-4 w-[calc(100%-2rem)] sm:max-w-[640px]'>

                <h1 className='md:text-4xl text-3xl text-primary font-bold text-center mb-8'>パスワード設定</h1>

                {isConfirmOk ?
                    (
                        <div className='space-y-6'>
                            <div className='bg-[#EDF8F3] p-6 rounded flex flex-col items-center gap-4'>
                                <img className='h-20' src={SuccessIcon} alt='success-icon' />
                                <div className='text-center font-medium'>
                                    パスワードの設定が完了しました。
                                </div>
                            </div>

                            <Button
                                type='button'
                                className='w-full h-14 text-base'
                                onClick={handleBackToLogin}
                            >
                                ログイン
                            </Button>
                        </div>
                    ) :
                    (
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
                                <div className='text-center text-sm text-text-secondary mb-4'>
                                    新しいパスワードを入力してください
                                </div>

                                <FormField
                                    control={form.control}
                                    name='password'
                                    render={({ field }) => (
                                        <FormItem className='space-y-2'>
                                            <Label htmlFor='password' required>新しいパスワード</Label>
                                            <FormControl>
                                                <PasswordInput
                                                    id='password'
                                                    type={!showNewPassword ? 'password' : 'text'}
                                                    maxLength={255}
                                                    autoComplete='off'
                                                    className='h-14'
                                                    onToggleIcon={() => setShowNewPassword(!showNewPassword)}
                                                    {...field}
                                                    onChange={(value) => {
                                                        field.onChange(value);
                                                        form.trigger(['password']);
                                                        if (form.getValues('passwordConfirm')?.length > 0) {
                                                            form.trigger('passwordConfirm');
                                                        }
                                                    }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name='passwordConfirm'
                                    render={({ field }) => (
                                        <FormItem className='space-y-2'>
                                            <Label htmlFor='passwordConfirm' required>新しいパスワードの確認</Label>
                                            <FormControl>
                                                <PasswordInput
                                                    id='passwordConfirm'
                                                    type={!showConfirmNewPassword ? 'password' : 'text'}
                                                    maxLength={255}
                                                    autoComplete='off'
                                                    className='h-14'
                                                    onPaste={e => e.preventDefault()}
                                                    onToggleIcon={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <Button
                                    type='submit'
                                    disabled={isLoading}
                                    className='w-full h-14 text-base'
                                >
                                    {isLoading ? '送信中...' : 'パスワードリセット'}
                                </Button>

                                <div className='text-center'>
                                    <Link
                                        to={ADMIN_ROUTES.login.href}
                                        className='inline-flex items-center gap-1.5 font-medium group text-gray-700 hover:text-gray-900'
                                    >
                                        <ArrowLeft
                                            size={20}
                                            strokeWidth={1.5}
                                            className='transition-transform duration-300 group-hover:-translate-x-1'
                                        />
                                        ログインに戻る
                                    </Link>
                                </div>
                            </form>
                        </Form>
                    )}
            </div>
        </div>
    );
});

export default ConfirmPasswordPage;
